import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDownLeft,
  ArrowUpRight,
  History,
  PackageX,
  Scale,
  Warehouse,
} from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import type { StockBalanceSummary } from '@aeris/contracts'
import { ZERO_QUANTITY, formatQuantity, parseQuantity } from '@aeris/inventory'
import { formatMoney } from '@aeris/money'
import { ZERO_AMOUNT, parseAmount } from '@aeris/sales'
import {
  Button,
  ColumnChooser,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  RowActions,
  RowOpener,
  SearchInput,
  SearchModeToggle,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  SortableHeaderCell,
  TableHeaderCell,
  TableRow,
  VisuallyHidden,
} from '@aeris/ui'

import { StockHistoryDialog } from '../components/stock-history-dialog.tsx'
import {
  StockMovementForm,
  type StockOperation,
} from '../components/stock-movement-form.tsx'
import { StockReportButton } from '../components/stock-report-button.tsx'
import { fetchBrands } from '../lib/api/brands.ts'
import { fetchProductCategories } from '../lib/api/product-categories.ts'
import {
  adjustStock,
  fetchBranches,
  fetchCompany,
  fetchStockBalances,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { categoryLabel } from '../lib/category-label.ts'
import {
  DEFAULT_STOCK_COLUMNS,
  STOCK_COLUMNS,
  type StockColumn,
  type StockSortKey,
} from '../lib/stock-columns.tsx'
import { stockScreenNotice } from '../lib/stock-policy.ts'
import {
  availableColumns,
  shownColumnsOf,
  toggledColumns,
} from '../lib/table-columns.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { usePreference } from '../lib/use-preference.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'

/**
 * Stock, per branch.
 *
 * Follows the pattern `/produtos` established: server-side search, server-side
 * pagination, a page size the client cannot raise past the server's cap.
 *
 * Two things distinguish it. First, everything here is scoped to a branch,
 * because stock is held somewhere and a tenant-wide "total stock" figure is a
 * number nobody can act on. Second, no figure on this screen is editable: a
 * balance changes only by recording a movement, and the history behind every
 * row is one click away. That is the ledger made visible.
 *
 * Write buttons are hidden without the permission. Hiding only — the API
 * refuses each request independently.
 */

const PAGE_SIZE = 25

/** O dinheiro do estoque (F163), pelo mesmo caminho do resto do sistema. */
const money = (value: string): string =>
  formatMoney(parseAmount(value) ?? ZERO_AMOUNT)

/**
 * Um número do topo do Estoque (F163).
 *
 * Não é o `StatTile` do painel: ali cada cartão é um bloco clicável de um
 * resumo; aqui são quatro números de uma linha só, acima de uma tabela, e um
 * cartão para cada um empurraria a lista para fora da tela.
 */
const StockTotal = ({
  label,
  value,
  note,
}: {
  label: string
  value: string
  note?: string | undefined
}): ReactNode => (
  <div>
    <p className="text-(length:--text-xs) text-(--text-muted)">{label}</p>
    <p className="font-(--font-mono) text-(length:--text-lg) text-(--text-primary)">
      {value}
    </p>
    {note === undefined ? null : (
      <p className="text-(length:--text-xs) text-(--text-muted)">{note}</p>
    )}
  </div>
)

export const EstoquePage = (): ReactNode => {
  const { allows, session } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [branchId, setBranchId] = useState<string>(
    session?.context?.branchId ?? ''
  )
  /**
   * O que a lista mostra (F044).
   *
   * Três valores e não dois booleanos: "somente com saldo" e "só o que ficou
   * negativo" são a mesma pergunta respondida de jeitos diferentes, e dois
   * interruptores independentes permitiriam a combinação que não quer dizer
   * nada.
   */
  const [situacao, setSituacao] = useState<
    'all' | 'with' | 'negative' | 'below'
  >('all')
  const onlyWithStock = situacao === 'with'
  const acertando = situacao === 'negative'
  /** A lista de reposição (F134): o que tem mínimo e está abaixo dele. */
  const repondo = situacao === 'below'
  /** A família da peça (F163). Vazio é "todas". */
  const [categoryId, setCategoryId] = useState('')
  /** A marca (F167). Vazio é "todas". */
  const [brand, setBrand] = useState('')
  const [page, setPage] = useState(1)

  // The list goes back to the first page when the column changes: a new
  // order with the old offset shows rows that have nothing to do with
  // what was just clicked.
  const sort = useTableSort<StockSortKey>('name', 'asc', () => {
    setPage(1)
  })
  const [operation, setOperation] = useState<StockOperation | null>(null)
  const [selected, setSelected] = useState<StockBalanceSummary | null>(null)
  const [historyFor, setHistoryFor] = useState<StockBalanceSummary | null>(null)

  // 300 ms: long enough that a typed word is one request, short enough that the
  // list feels attached to the keyboard.
  const debouncedSearch = useDebounced(search)

  /**
   * A loja não conta estoque, e a tela diz isso antes de qualquer número
   * (F102).
   *
   * A linha do menu já sumiu, mas a rota continua existindo — um link antigo,
   * um favorito, o Ctrl+K. Abrir uma lista de saldos sem dizer que eles pararam
   * de se mover é a mesma mentira que a F070 recusou, só que numa tela onde ela
   * custa mais: aqui os números parecem atuais.
   */
  const companyId = session?.context?.companyId ?? null
  const company = useQuery({
    queryKey: ['company', companyId],
    queryFn: ({ signal }) => fetchCompany(companyId ?? '', signal),
    enabled: companyId !== null,
  })

  const stockOff =
    company.data !== undefined && !company.data.company.salesFlow.countsStock
      ? stockScreenNotice(
          company.data.company.tradeName ?? company.data.company.legalName
        )
      : null

  /*
   * As ações de escrita somem junto com a política.
   *
   * O servidor recusa com `stock_not_counted` de qualquer jeito (F102); tirar
   * os botões aqui é para a pessoa não digitar uma contagem inteira antes de
   * ouvir "não". A LEITURA continua: o histórico e os saldos que ficaram são
   * fato, e escondê-los seria apagar o passado em vez de parar o presente.
   */
  const canWrite = allows('inventory.write') && stockOff === null
  const canAdjust = allows('inventory.adjust') && stockOff === null

  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: ({ signal }) => fetchBranches(undefined, signal),
  })

  /*
   * As categorias cadastradas (F163).
   *
   * A lista inteira de uma vez — são punhados —, e o seletor só aparece quando
   * existe o que escolher: numa loja que nunca criou categoria, um filtro vazio
   * é ruído no lugar onde o caixa procura a busca.
   */
  const categories = useQuery({
    queryKey: ['product-categories', 'active'],
    queryFn: ({ signal }) => fetchProductCategories('active', signal),
  })

  /*
   * As marcas cadastradas (F158), para o filtro (F167).
   *
   * A lista inteira de uma vez, como no formulário do produto: são dezenas, e
   * pedir uma busca por letra digitada para escolher entre trinta nomes seria
   * uma requisição por tecla para resolver um menu.
   */
  const brands = useQuery({
    queryKey: ['brands', 'active'],
    queryFn: ({ signal }) => fetchBrands('active', signal),
  })

  /*
   * As colunas desta pessoa (F167), no servidor como as de Produtos (F127):
   * valem em qualquer computador em que ela entrar. Quem nunca escolheu vê as
   * de sempre.
   */
  const columnsPreference = usePreference('table.stock.columns', {
    visible: [...DEFAULT_STOCK_COLUMNS],
  })
  const offeredColumns = availableColumns(STOCK_COLUMNS, allows)
  const shownColumns: StockColumn[] = shownColumnsOf(
    offeredColumns,
    columnsPreference.value.visible
  )

  /** Onde a busca procura (F167). A escolha viaja com a pessoa. */
  const searchModePreference = usePreference('search.mode', {
    products: 'anywhere',
    stock: 'anywhere',
  })
  const searchMode = searchModePreference.value.stock

  const query = useQuery({
    queryKey: [
      'stock-balances',
      {
        search: debouncedSearch,
        searchMode,
        branchId,
        situacao,
        categoryId,
        brand,
        page,
        ...sortKey(sort),
      },
    ],
    queryFn: ({ signal }) =>
      fetchStockBalances(
        {
          ...(debouncedSearch.trim().length > 0
            ? { search: debouncedSearch.trim(), searchMode }
            : {}),
          ...(branchId.length > 0 ? { branchId } : {}),
          onlyWithStock,
          negative: acertando,
          belowMinimum: repondo,
          ...(categoryId === '' ? {} : { categoryId }),
          ...(brand === '' ? {} : { brand }),
          page,
          pageSize: PAGE_SIZE,
          sort: sort.sort,
          direction: sort.direction,
        },
        signal
      ),
    // Keeps the previous page on screen while the next one loads, so the table
    // does not collapse to a spinner on every keystroke.
    placeholderData: (previous) => previous,
  })

  const total = query.data?.total ?? 0
  const totals = query.data?.totals
  const items = query.data?.items ?? []
  const searching = debouncedSearch.trim().length > 0

  /*
   * O que cada célula precisa saber — F190.
   *
   * A busca viaja junto para que a linha que casou só pelo código de barras
   * possa dizer isso. Montado uma vez, e não por célula, porque o objeto novo
   * a cada linha faria o React redesenhar a tabela inteira sem necessidade.
   */
  const cellContext = useMemo(
    () => ({ onOpen: setHistoryFor, search: debouncedSearch.trim() }),
    [debouncedSearch]
  )

  /**
   * "Não tem nenhum" — o botão que esta loja vai apertar mais (F044).
   *
   * A Elite Digital não sabe quanto tem em estoque: os 5309 produtos vieram do
   * GDOOR sem contagem. O caminho real, na frase do dono, é *"o cara cancela a
   * venda porque não tem a peça, marca como sem estoque e pronto, resolveu"* —
   * e depois eles vão contando aos poucos o que interessa.
   *
   * Um clique grava uma **contagem de zero**, que é um ajuste como qualquer
   * outro — com autor, data e motivo na auditoria. Desde a F132 ele pede
   * confirmação (grava no estoque, e a linha ao lado é de outro produto) e
   * diz quando não conseguiu, em vez de não fazer nada em silêncio.
   */
  const [zeroing, setZeroing] = useState<StockBalanceSummary | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const zerar = useMutation({
    mutationFn: async (balance: StockBalanceSummary) =>
      await adjustStock({
        variantId: balance.variantId,
        ...(branchId.length > 0 ? { branchId } : {}),
        counted: '0',
        note: 'Não tinha nenhum na prateleira',
      }),
    onSuccess: async () => {
      setZeroing(null)
      setActionError(null)
      await queryClient.invalidateQueries({ queryKey: ['stock-balances'] })
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: () => {
      setZeroing(null)
      setActionError(
        'Não foi possível gravar a contagem de zero. Verifique a conexão e tente de novo.'
      )
    },
  })

  const open = (
    balance: StockBalanceSummary,
    nextOperation: StockOperation
  ): void => {
    setSelected(balance)
    setOperation(nextOperation)
  }

  const closeForm = (): void => {
    setOperation(null)
    setSelected(null)
  }

  return (
    <Page>
      <PageHeader
        title="Estoque"
        description="O saldo é calculado a partir das movimentações. Nenhum número aqui é editado diretamente."
        actions={
          <StockReportButton
            sort={{ sort: sort.sort, direction: sort.direction }}
            branchId={branchId}
            branchName={
              (branches.data?.branches ?? []).find(
                (branch) => branch.id === branchId
              )?.name ?? 'Filial'
            }
            filters={{
              search,
              searchMode,
              situacao,
              ...(brand === '' ? {} : { brand }),
              ...(categoryId === ''
                ? {}
                : {
                    categoryId,
                    categoryName:
                      categories.data?.find(
                        (category) => category.id === categoryId
                      )?.name ?? '',
                  }),
            }}
          />
        }
      />

      {stockOff === null ? null : (
        <InlineAlert tone="warning">{stockOff}</InlineAlert>
      )}

      <ListToolbar
        actions={
          <ColumnChooser
            columns={offeredColumns}
            visible={shownColumns.map((column) => column.id)}
            onToggle={(id) => {
              columnsPreference.save({
                visible: toggledColumns(columnsPreference.value.visible, id),
              })
            }}
            onReset={() => {
              columnsPreference.save({ visible: [...DEFAULT_STOCK_COLUMNS] })
            }}
          />
        }
      >
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            // A new search term always means page one, and doing it in the
            // handler avoids a render cascade.
            setPage(1)
          }}
          placeholder="Buscar por nome, código ou código de barras"
          aria-label="Buscar produtos no estoque"
        />

        <SearchModeToggle
          mode={searchMode}
          onChange={(next) => {
            searchModePreference.save({
              ...searchModePreference.value,
              stock: next,
            })
            setPage(1)
          }}
        />

        <Select
          value={branchId}
          onChange={(event) => {
            setBranchId(event.target.value)
            setPage(1)
          }}
          aria-label="Filial"
          className="w-56"
        >
          {/* Stock is held somewhere. There is no "all branches" option,
              because a combined figure is not something anybody can act on. */}
          {(branches.data?.branches ?? []).map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name}
            </option>
          ))}
        </Select>

        <Select
          value={situacao}
          onChange={(event) => {
            setSituacao(
              event.target.value as 'all' | 'with' | 'negative' | 'below'
            )
            setPage(1)
          }}
          aria-label="Situação do saldo"
          className="w-56"
        >
          <option value="all">Todos os produtos</option>
          <option value="with">Somente com saldo</option>
          {/*
            "A acertar" e não "Saldo negativo": o nome diz o que fazer, não o
            que aconteceu. Quem abre esta tela às sete da manhã quer uma lista
            de trabalho, não um relatório.
          */}
          <option value="negative">A acertar (saldo negativo)</option>
          <option value="below">Abaixo do mínimo</option>
        </Select>

        {/*
          A família da peça (F163): "quantas baterias eu tenho, e quanto isso
          dá em dinheiro" é a pergunta que o dono faz nesta tela.
        */}
        {(categories.data ?? []).length === 0 ? null : (
          <Select
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value)
              setPage(1)
            }}
            aria-label="Categoria"
            className="w-56"
          >
            <option value="">Todas as categorias</option>
            {(categories.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {categoryLabel(category)}
              </option>
            ))}
          </Select>
        )}

        {/* A marca (F167), pelo cadastro da F158 — nunca texto livre. */}
        {(brands.data ?? []).length === 0 ? null : (
          <Select
            value={brand}
            onChange={(event) => {
              setBrand(event.target.value)
              setPage(1)
            }}
            aria-label="Marca"
            className="w-56"
          >
            <option value="">Todas as marcas</option>
            {(brands.data ?? []).map((item) => (
              <option key={item.id} value={item.name}>
                {item.name}
              </option>
            ))}
          </Select>
        )}
      </ListToolbar>

      {/*
        A explicação vale mais que a tela aqui.

        Saldo negativo parece um erro do sistema, e não é: é uma regra do
        produto funcionando. Quando a mesma peça foi vendida no balcão e no
        computador da loja durante uma queda de internet, **nenhuma venda
        perde** — as duas valem, o saldo vai abaixo de zero, e o sistema
        reporta em vez de escolher qual cliente ficou sem a mercadoria.
      */}
      {acertando ? (
        <InlineAlert
          tone="info"
          title="Saldo abaixo de zero: mais saídas registradas do que entradas — quase sempre a peça foi vendida sem estar contada."
        >
          Não é um erro a corrigir com pressa. Conte a prateleira quando passar
          por ela: se não tiver nenhuma, &quot;Não tem nenhum&quot; resolve; se
          tiver, use Ajustar e informe quanto contou.
        </InlineAlert>
      ) : null}

      <ResultCount
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        loading={query.isPending}
        none="Nenhum produto encontrado"
      />

      {/*
        Quanto tem, e quanto vale (F163).

        Do FILTRO inteiro, não da página: escolher "Bateria" e ver o valor de 25
        linhas seria pior que não ver valor nenhum. Os avisos de "sem custo" e
        "sem preço" ficam junto do número que eles afetam, porque um lucro
        calculado sem metade dos custos é um número bonito e falso.
      */}
      {totals === undefined ? null : (
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <StockTotal
            label="Unidades"
            value={formatQuantity(parseQuantity(totals.units) ?? ZERO_QUANTITY)}
          />
          <StockTotal
            label="Custo"
            value={money(totals.costValue)}
            note={
              totals.missingCost === 0
                ? undefined
                : `${String(totals.missingCost)} ${totals.missingCost === 1 ? 'peça' : 'peças'} sem custo cadastrado`
            }
          />
          <StockTotal
            label="Preço de venda"
            value={money(totals.saleValue)}
            note={
              totals.missingPrice === 0
                ? undefined
                : `${String(totals.missingPrice)} ${totals.missingPrice === 1 ? 'peça' : 'peças'} sem preço`
            }
          />
          <StockTotal
            label="Lucro possível"
            value={money(totals.profitValue)}
            note={
              totals.marginPercent === null
                ? undefined
                : `margem de ${totals.marginPercent.replace('.', ',')}%`
            }
          />
        </div>
      )}

      {actionError === null ? null : (
        <InlineAlert tone="danger">{actionError}</InlineAlert>
      )}

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="o estoque"
        isEmpty={items.length === 0}
        filtered={
          searching || situacao !== 'all' || categoryId !== '' || brand !== ''
        }
        onClearFilters={() => {
          // A situação também: "Somente com saldo" sem resultado dizia
          // "um produto aparece aqui depois da primeira entrada", e o botão
          // limpava só a busca.
          //
          // Categoria e marca entraram na conta na F182: elas existiam na
          // barra e ficavam de fora daqui, então a tela vazia por causa de uma
          // marca escolhida não se dizia filtrada — e o botão "Limpar filtros",
          // quando aparecia, deixava a marca de pé e não limpava nada.
          setSearch('')
          setSituacao('all')
          setCategoryId('')
          setBrand('')
          setPage(1)
        }}
        empty={
          <EmptyState
            icon={<Warehouse aria-hidden="true" className="h-8 w-8" />}
            title={
              acertando
                ? 'Nada a acertar nesta filial'
                : repondo
                  ? 'Nada abaixo do mínimo nesta filial'
                  : onlyWithStock
                    ? 'Nenhum produto com saldo nesta filial'
                    : 'Nenhuma movimentação nesta filial'
            }
            description={
              acertando
                ? 'Nenhum produto está com saldo abaixo de zero. É assim que deve ficar.'
                : repondo
                  ? 'Só entra aqui o produto que tem estoque mínimo, definido na ficha dele.'
                  : canWrite
                    ? 'Um produto aparece aqui depois da primeira entrada de estoque.'
                    : 'Você não tem permissão para movimentar o estoque.'
            }
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {/* Cabeçalho e linha saem da MESMA lista: uma coluna ligada no
                    menu não tem como aparecer num dos dois e faltar no outro. */}
                {shownColumns.map((column) =>
                  column.sortKey === undefined ? (
                    <TableHeaderCell
                      key={column.id}
                      numeric={column.numeric === true}
                      className={column.headerClassName}
                    >
                      {column.label}
                    </TableHeaderCell>
                  ) : (
                    <SortableHeaderCell
                      key={column.id}
                      numeric={column.numeric === true}
                      {...sort.column(column.sortKey)}
                    >
                      {column.label}
                    </SortableHeaderCell>
                  )
                )}
                <TableHeaderCell>
                  <VisuallyHidden>Ações</VisuallyHidden>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((balance) => {
                /*
                 * Um botão à vista e o resto no ⋯ (decisão do dono, F132).
                 * O à vista é o que esta lista pede: na lista "a acertar",
                 * "Não tem nenhum" (F044); no resto, Ajustar — e Histórico
                 * para quem não pode ajustar.
                 */
                const zeroFirst = acertando && canAdjust
                const primary = zeroFirst ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setZeroing(balance)
                    }}
                    leadingIcon={
                      <PackageX aria-hidden="true" className="h-3.5 w-3.5" />
                    }
                  >
                    Não tem nenhum
                  </Button>
                ) : canAdjust ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      open(balance, 'adjustment')
                    }}
                    leadingIcon={
                      <Scale aria-hidden="true" className="h-3.5 w-3.5" />
                    }
                  >
                    Ajustar
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setHistoryFor(balance)
                    }}
                    leadingIcon={
                      <History aria-hidden="true" className="h-3.5 w-3.5" />
                    }
                  >
                    Histórico
                  </Button>
                )

                return (
                  /*
                   * Clicar na linha abre o HISTÓRICO, e não uma ficha nova
                   * (F151/F154).
                   *
                   * O saldo já está inteiro na tabela: dos 12 campos do
                   * `StockBalanceSummary`, 8 estão à vista e os outros são
                   * identificadores. Uma ficha de leitura aqui seria um
                   * diálogo praticamente vazio, e ensinaria que "clicar na
                   * linha" às vezes não vale a pena.
                   *
                   * O que falta a quem clica num saldo é sempre a mesma
                   * pergunta — "por que está nesse número?" —, e é o razão do
                   * produto que responde.
                   */
                  <TableRow
                    key={balance.variantId}
                    onOpen={() => {
                      setHistoryFor(balance)
                    }}
                  >
                    {shownColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        numeric={column.numeric === true}
                        className={column.className}
                      >
                        {/*
                          O nome leva o abridor da linha, e só ele: `RowOpener`
                          é o alvo de teclado que abre o histórico, e repeti-lo
                          em cada célula daria doze paradas de Tab por linha.
                        */}
                        {column.id === 'name' ? (
                          <RowOpener
                            onOpen={() => {
                              setHistoryFor(balance)
                            }}
                          >
                            {column.cell(balance, cellContext)}
                          </RowOpener>
                        ) : (
                          column.cell(balance, cellContext)
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      <RowActions
                        name={balance.productName}
                        primary={primary}
                        items={[
                          zeroFirst && {
                            label: 'Ajustar',
                            icon: (
                              <Scale aria-hidden="true" className="h-4 w-4" />
                            ),
                            onSelect: () => {
                              open(balance, 'adjustment')
                            },
                          },
                          canWrite && {
                            label: 'Entrada',
                            icon: (
                              <ArrowDownLeft
                                aria-hidden="true"
                                className="h-4 w-4"
                              />
                            ),
                            onSelect: () => {
                              open(balance, 'entry')
                            },
                          },
                          canWrite && {
                            label: 'Saída',
                            icon: (
                              <ArrowUpRight
                                aria-hidden="true"
                                className="h-4 w-4"
                              />
                            ),
                            onSelect: () => {
                              open(balance, 'exit')
                            },
                          },
                          canAdjust && {
                            label: 'Histórico',
                            icon: (
                              <History aria-hidden="true" className="h-4 w-4" />
                            ),
                            onSelect: () => {
                              setHistoryFor(balance)
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmDialog
        open={zeroing !== null}
        onOpenChange={(next) => {
          if (!next) setZeroing(null)
        }}
        tone="neutral"
        title="Não tem nenhum"
        description={
          zeroing === null
            ? ''
            : `Gravar uma contagem de zero para ${zeroing.productName} (${zeroing.sku})? O saldo desta filial passa a 0, e o ajuste fica no histórico com o seu nome.`
        }
        confirmLabel="Gravar zero"
        loading={zerar.isPending}
        onConfirm={() => {
          if (zeroing !== null) zerar.mutate(zeroing)
        }}
      />

      {/* Mounted only while open and keyed by the row, so the form starts from
          the right values without an effect that resets them. */}
      {operation !== null && selected !== null ? (
        <StockMovementForm
          key={`${selected.variantId}-${operation}`}
          operation={operation}
          balance={selected}
          onClose={closeForm}
        />
      ) : null}

      {historyFor === null ? null : (
        <StockHistoryDialog
          key={historyFor.variantId}
          balance={historyFor}
          onClose={() => {
            setHistoryFor(null)
          }}
        />
      )}
    </Page>
  )
}
